"use client"

import { ArrowLeft, Shield, FileText, Scale, AlertTriangle, Search, Eye, BookOpen, Globe, GraduationCap, Mail, Layers, Users, FileCheck, Landmark } from "lucide-react"
import Link from "next/link"

export default function AMLPolicyPage() {
  const lastUpdated = "March 1, 2026"
  const effectiveDate = "March 1, 2026"

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <header className="border-b border-border dark:border-white/[0.06] bg-background/80 backdrop-blur-xl sticky top-0 z-50">
        <div className="max-w-4xl mx-auto px-6 py-4 flex items-center justify-between">
          <Link href="/auth/signin" className="flex items-center gap-2 text-muted-foreground hover:text-foreground transition-colors">
            <ArrowLeft className="w-4 h-4" />
            <span className="text-sm">Back to Sign In</span>
          </Link>
          <div className="flex items-center gap-1">
            {/* eslint-disable-next-line @next/next/no-img-element */}
            <img src="/favicon.svg" alt="FluxPay" className="w-6 h-6" />
            <span className="font-semibold tracking-wide uppercase text-sm hidden dark:inline" style={{ background: 'linear-gradient(135deg, #e0f4ff 0%, #ffffff 25%, #b0d8ff 50%, #ffffff 75%, #90b0e0 100%)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent', backgroundClip: 'text' }}>FluxPay</span>
            <span className="font-semibold tracking-wide uppercase text-sm inline dark:hidden" style={{ background: 'linear-gradient(135deg, #0e7490 0%, #155e75 25%, #1D3557 50%, #0891b2 75%, #164e63 100%)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent', backgroundClip: 'text' }}>FluxPay</span>
          </div>
        </div>
      </header>

      {/* Content */}
      <main className="max-w-4xl mx-auto px-6 py-12">
        {/* Title Section */}
        <div className="mb-12">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-12 h-12 rounded-xl bg-cyan-500/10 flex items-center justify-center">
              <Shield className="w-6 h-6 text-cyan-400" />
            </div>
            <div>
              <h1 className="text-3xl font-bold text-foreground">AML/KYC Policy</h1>
              <p className="text-muted-foreground text-sm">Anti-Money Laundering, Know Your Customer & Counter-Terrorist Financing</p>
            </div>
          </div>
          <div className="flex items-center gap-4 text-sm text-muted-foreground">
            <span>Last Updated: {lastUpdated}</span>
            <span>|</span>
            <span>Effective: {effectiveDate}</span>
          </div>
        </div>

        {/* Quick Navigation */}
        <div className="mb-12 p-6 rounded-2xl bg-card/80 dark:bg-white/[0.02] border border-border dark:border-white/[0.06]">
          <h2 className="text-sm font-semibold text-muted-foreground uppercase tracking-wider mb-4">Quick Navigation</h2>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
            {[
              { label: "Introduction", href: "#introduction" },
              { label: "Regulatory Framework", href: "#regulatory" },
              { label: "ML Typologies", href: "#typologies" },
              { label: "Customer Due Diligence", href: "#cdd" },
              { label: "Enhanced Due Diligence", href: "#edd" },
              { label: "Ongoing Monitoring", href: "#monitoring" },
              { label: "Transaction Reporting", href: "#str" },
              { label: "Large Transaction Reports", href: "#large-transactions" },
              { label: "Record Keeping", href: "#records" },
              { label: "Sanctions Screening", href: "#sanctions" },
              { label: "Ministerial Directives", href: "#ministerial" },
              { label: "Training & Compliance", href: "#training" },
              { label: "Contact", href: "#contact" },
            ].map((item) => (
              <a
                key={item.href}
                href={item.href}
                className="px-4 py-2 rounded-lg bg-card/80 dark:bg-white/[0.03] border border-border dark:border-white/[0.06] text-sm text-muted-foreground hover:text-foreground hover:border-cyan-500/30 transition-all"
              >
                {item.label}
              </a>
            ))}
          </div>
        </div>

        {/* Legal Content */}
        <div className="prose prose-invert prose-gray max-w-none">
          {/* Section 1 */}
          <section id="introduction" className="mb-12">
            <h2 className="text-xl font-semibold text-foreground mb-4 flex items-center gap-2">
              <FileText className="w-5 h-5 text-cyan-400" />
              1. Introduction
            </h2>
            <div className="space-y-4 text-muted-foreground leading-relaxed">
              <p>
                FluxPay, a trade name of Ideation House Financial Services Limited (CBCA Corporation No. 1419582-5, FINTRAC MSB Registration No. M22512329), is committed to the highest standards of Anti-Money Laundering (AML), Know Your Customer (KYC), and Counter-Terrorist Financing (CTF) compliance. As a registered Money Services Business (MSB) with FINTRAC, we maintain comprehensive policies and procedures to detect and prevent financial crime.
              </p>
              <p>
                Our registered MSB services include Foreign Exchange Dealing and Money Transferring.
              </p>
            </div>
          </section>

          {/* Section 2 */}
          <section id="regulatory" className="mb-12">
            <h2 className="text-xl font-semibold text-foreground mb-4 flex items-center gap-2">
              <Scale className="w-5 h-5 text-cyan-400" />
              2. Regulatory Framework
            </h2>
            <div className="space-y-4 text-muted-foreground leading-relaxed">
              <p>
                FluxPay operates in compliance with the following Canadian federal legislation and regulations:
              </p>
              <ul className="list-disc pl-6 space-y-2">
                <li>Proceeds of Crime (Money Laundering) and Terrorist Financing Act (PCMLTFA)</li>
                <li>Proceeds of Crime (Money Laundering) and Terrorist Financing Regulations (PCMLTFR)</li>
                <li>FINTRAC Guidance and Directives</li>
                <li>Criminal Code of Canada (Part II.1 — Terrorism)</li>
                <li>United Nations Act (UNA)</li>
                <li>Special Economic Measures Act (SEMA)</li>
                <li>Justice for Victims of Corrupt Foreign Officials Act (JVCFOA)</li>
              </ul>
              <div className="p-4 rounded-xl bg-amber-500/10 border border-amber-500/20">
                <p className="text-amber-200 text-sm">
                  <strong>Important:</strong> FluxPay is registered with FINTRAC as a Money Services Business (MSB Registration No. M22512329). We are subject to examination by FINTRAC and must comply with all applicable reporting and record-keeping requirements.
                </p>
              </div>
            </div>
          </section>

          {/* Section 3 - Money Laundering Typologies */}
          <section id="typologies" className="mb-12">
            <h2 className="text-xl font-semibold text-foreground mb-4 flex items-center gap-2">
              <Layers className="w-5 h-5 text-cyan-400" />
              3. Money Laundering Typologies
            </h2>
            <div className="space-y-4 text-muted-foreground leading-relaxed">
              <p>
                Money laundering typically involves three distinct stages. Understanding these stages is critical to FluxPay&apos;s ability to detect and prevent the misuse of its payment processing services.
              </p>

              <h3 className="text-lg font-medium text-foreground mt-6">3.1 Placement</h3>
              <p>
                The introduction of criminal proceeds into the financial system. Common placement techniques include:
              </p>
              <ul className="list-disc pl-6 space-y-2">
                <li>Structuring deposits below reporting thresholds to avoid detection</li>
                <li>Purchasing monetary instruments (money orders, prepaid cards) with illicit cash</li>
                <li>Commingling criminal proceeds with legitimate business funds</li>
                <li>Using cash-intensive businesses to disguise the origin of funds</li>
              </ul>

              <h3 className="text-lg font-medium text-foreground mt-6">3.2 Layering</h3>
              <p>
                Separating the proceeds from their criminal source through complex layers of financial transactions designed to obscure the audit trail:
              </p>
              <ul className="list-disc pl-6 space-y-2">
                <li>Wire transfers between multiple accounts across jurisdictions</li>
                <li>Converting funds between currencies to create distance from the source</li>
                <li>Transactions through shell companies and nominee accounts</li>
                <li>Use of complex corporate structures to hide beneficial ownership</li>
              </ul>

              <h3 className="text-lg font-medium text-foreground mt-6">3.3 Integration</h3>
              <p>
                Re-entering laundered funds into the legitimate economy so they appear to come from lawful sources:
              </p>
              <ul className="list-disc pl-6 space-y-2">
                <li>Real estate purchases and property investments</li>
                <li>Acquisition of luxury goods and high-value assets</li>
                <li>Business investments and capital infusions into legitimate enterprises</li>
                <li>Loan-back schemes using laundered funds as collateral</li>
              </ul>

              <h3 className="text-lg font-medium text-foreground mt-6">3.4 Techniques Relevant to Payment Processing</h3>
              <p>
                FluxPay is specifically vigilant for laundering techniques that exploit payment processing infrastructure:
              </p>
              <ul className="list-disc pl-6 space-y-2">
                <li>Rapid movement of funds through multiple payment channels in a short time period</li>
                <li>Use of multiple merchant accounts to structure transactions below monitoring thresholds</li>
                <li>Unusual refund patterns, such as processing payments and immediately issuing refunds to different accounts</li>
                <li>Transaction volumes or patterns inconsistent with a merchant&apos;s stated business type and size</li>
                <li>Round-dollar transactions or repetitive identical amounts with no commercial rationale</li>
              </ul>

              <div className="p-4 rounded-xl bg-cyan-500/10 border border-cyan-500/20">
                <p className="text-cyan-200 text-sm">
                  <strong>Note:</strong> FluxPay&apos;s transaction monitoring system is calibrated to detect indicators of all three stages of money laundering. Employees are trained to recognize red flags specific to each stage as part of their AML/CTF obligations.
                </p>
              </div>
            </div>
          </section>

          {/* Section 4 */}
          <section id="cdd" className="mb-12">
            <h2 className="text-xl font-semibold text-foreground mb-4 flex items-center gap-2">
              <Search className="w-5 h-5 text-cyan-400" />
              4. Customer Due Diligence (CDD)
            </h2>
            <div className="space-y-4 text-muted-foreground leading-relaxed">
              <h3 className="text-lg font-medium text-foreground">4.1 Know Your Business (KYB) Procedures</h3>
              <p>Before onboarding any merchant, FluxPay conducts comprehensive due diligence:</p>
              <ul className="list-disc pl-6 space-y-2">
                <li>Verification of business registration and legal status</li>
                <li>Confirmation of Existence using the Confirmation of Existence Method as prescribed by FINTRAC</li>
                <li>Collection and verification of beneficial ownership information</li>
                <li>Assessment of the nature and purpose of the business relationship</li>
                <li>Verification of authorized signatories</li>
              </ul>

              <h3 className="text-lg font-medium text-foreground mt-6">4.2 Identity Verification</h3>
              <p>We verify the identity of all directors, officers, and beneficial owners through:</p>
              <ul className="list-disc pl-6 space-y-2">
                <li>Government-issued photo identification (passport, driver's license, or equivalent)</li>
                <li>Independent electronic verification methods</li>
                <li>Dual-process verification for high-risk relationships</li>
                <li>Ongoing verification as required by FINTRAC guidelines</li>
              </ul>

              <h3 className="text-lg font-medium text-foreground mt-6">4.3 Business Relationship Assessment</h3>
              <p>For each merchant relationship, we assess:</p>
              <ul className="list-disc pl-6 space-y-2">
                <li>Expected transaction volumes and patterns</li>
                <li>Geographic risk factors</li>
                <li>Industry and business model risk</li>
                <li>Source of funds and wealth where applicable</li>
              </ul>

              <h3 className="text-lg font-medium text-foreground mt-6">4.4 Beneficial Ownership Identification</h3>
              <p>Per PCMLTFA requirements, FluxPay identifies and verifies beneficial owners for all merchant relationships:</p>
              <ul className="list-disc pl-6 space-y-2">
                <li>Identify all individuals who directly or indirectly own or control 25% or more of a corporation</li>
                <li>For entities where no individual holds 25% or more, identify the directors of the corporation</li>
                <li>Verify the identity of each beneficial owner before establishing the business relationship</li>
                <li>Re-verify beneficial ownership information when changes are detected or at least every five (5) years</li>
              </ul>
              <p>For each beneficial owner, FluxPay records:</p>
              <ul className="list-disc pl-6 space-y-2">
                <li>Full legal name</li>
                <li>Date of birth</li>
                <li>Residential address</li>
                <li>Nature and extent of ownership or control (e.g., percentage of shares, voting rights, or other means of control)</li>
              </ul>

              <h3 className="text-lg font-medium text-foreground mt-6">4.5 Third-Party Determination</h3>
              <p>
                FluxPay must determine whether a client is acting on behalf of a third party. When a reasonable suspicion exists that a merchant is processing payments for an undisclosed third party, FluxPay will:
              </p>
              <ul className="list-disc pl-6 space-y-2">
                <li>Request written confirmation of all third-party relationships</li>
                <li>Identify and verify the third party using standard CDD procedures</li>
                <li>Document the nature, purpose, and scope of the arrangement</li>
                <li>Apply enhanced monitoring to the relationship for the duration of the arrangement</li>
              </ul>
              <div className="p-4 rounded-xl bg-amber-500/10 border border-amber-500/20">
                <p className="text-amber-200 text-sm">
                  <strong>Important:</strong> Merchants are prohibited from processing payments on behalf of undisclosed third parties. Failure to disclose third-party relationships may result in immediate suspension of the merchant account and filing of a Suspicious Transaction Report.
                </p>
              </div>
            </div>
          </section>

          {/* Section 5 */}
          <section id="edd" className="mb-12">
            <h2 className="text-xl font-semibold text-foreground mb-4 flex items-center gap-2">
              <AlertTriangle className="w-5 h-5 text-cyan-400" />
              5. Enhanced Due Diligence (EDD)
            </h2>
            <div className="space-y-4 text-muted-foreground leading-relaxed">
              <p>Enhanced measures are applied in higher-risk situations including:</p>

              <h3 className="text-lg font-medium text-foreground mt-6">5.1 High-Risk Business Categories</h3>
              <ul className="list-disc pl-6 space-y-2">
                <li>Cannabis-related businesses (where legally permitted)</li>
                <li>iGaming and online gambling operators</li>
                <li>Money services businesses and payment facilitators</li>
                <li>Businesses operating in high-risk jurisdictions</li>
              </ul>

              <h3 className="text-lg font-medium text-foreground mt-6">5.2 Politically Exposed Persons (PEPs)</h3>
              <p>Per the PCMLTFA, FluxPay applies enhanced due diligence to the following categories of politically exposed persons:</p>

              <h4 className="text-base font-medium text-foreground mt-4">Foreign Politically Exposed Persons</h4>
              <p>A person who holds or has held one of the following positions in or on behalf of a foreign state:</p>
              <ul className="list-disc pl-6 space-y-2">
                <li>Head of state or head of government</li>
                <li>Member of the executive council of government or member of a legislature</li>
                <li>Deputy minister or equivalent</li>
                <li>Ambassador, or attach&eacute; or counsellor of an ambassador</li>
                <li>Military officer with a rank of general or above</li>
                <li>President of a state-owned company or bank</li>
                <li>Head of a government agency, court, or tribunal</li>
                <li>Judge of a supreme court, constitutional court, or other court of last resort</li>
              </ul>

              <h4 className="text-base font-medium text-foreground mt-4">Domestic Politically Exposed Persons</h4>
              <p>A person who holds or has held one of the following positions in Canada:</p>
              <ul className="list-disc pl-6 space-y-2">
                <li>Governor General, Lieutenant Governor, or head of government</li>
                <li>Member of the Senate or House of Commons, or member of a provincial legislature</li>
                <li>Deputy minister or equivalent</li>
                <li>Ambassador, or attach&eacute; or counsellor of an ambassador</li>
                <li>Military officer with a rank of general or above</li>
                <li>Mayor, judge of a federal or provincial court</li>
                <li>Head or president of a Crown corporation, government agency, or government body</li>
              </ul>

              <h4 className="text-base font-medium text-foreground mt-4">Heads of International Organizations (HIOs)</h4>
              <p>A person who is the head or deputy head of an international organization established by the governments of states, including but not limited to:</p>
              <ul className="list-disc pl-6 space-y-2">
                <li>United Nations and its specialized agencies</li>
                <li>World Bank, International Monetary Fund (IMF)</li>
                <li>World Trade Organization (WTO)</li>
                <li>North Atlantic Treaty Organization (NATO)</li>
                <li>Other treaty-based international organizations</li>
              </ul>

              <h4 className="text-base font-medium text-foreground mt-4">Family Members and Close Associates</h4>
              <ul className="list-disc pl-6 space-y-2">
                <li><strong>Family members:</strong> Spouse or common-law partner, child, child&apos;s spouse or common-law partner, sibling, parent, mother or father-in-law</li>
                <li><strong>Close associates:</strong> Persons who are known to be closely connected to a PEP for personal or business reasons</li>
              </ul>

              <h4 className="text-base font-medium text-foreground mt-4">Enhanced PEP Measures</h4>
              <ul className="list-disc pl-6 space-y-2">
                <li>PEP status monitoring continues for a minimum of five (5) years after the person has left office</li>
                <li>Senior management approval is required before establishing or continuing any business relationship with a PEP, HIO, or their family members and close associates</li>
                <li>Source of funds and source of wealth must be established and documented</li>
                <li>Enhanced ongoing monitoring is applied for the duration of the relationship</li>
              </ul>

              <h3 className="text-lg font-medium text-foreground mt-6">5.3 High-Risk Jurisdictions</h3>
              <ul className="list-disc pl-6 space-y-2">
                <li>Countries identified by FATF as having strategic deficiencies</li>
                <li>Jurisdictions subject to Canadian sanctions</li>
                <li>Countries with elevated corruption or terrorism financing risk</li>
              </ul>
            </div>
          </section>

          {/* Section 6 */}
          <section id="monitoring" className="mb-12">
            <h2 className="text-xl font-semibold text-foreground mb-4 flex items-center gap-2">
              <Eye className="w-5 h-5 text-cyan-400" />
              6. Ongoing Monitoring
            </h2>
            <div className="space-y-4 text-muted-foreground leading-relaxed">
              <h3 className="text-lg font-medium text-foreground">6.1 Transaction Monitoring</h3>
              <ul className="list-disc pl-6 space-y-2">
                <li>Automated monitoring of all transactions for unusual patterns</li>
                <li>Risk-based thresholds calibrated to merchant profiles</li>
                <li>Real-time alerts for transactions exceeding prescribed thresholds</li>
                <li>Review of large cash transactions and electronic funds transfers</li>
              </ul>

              <h3 className="text-lg font-medium text-foreground mt-6">6.2 Business Relationship Review</h3>
              <ul className="list-disc pl-6 space-y-2">
                <li>Periodic review of all merchant relationships</li>
                <li>Ongoing monitoring triggers per FINTRAC guidance (two or more transactions within a five-year period)</li>
                <li>Update of KYC documentation at regular intervals</li>
                <li>Reassessment of risk ratings based on transaction history</li>
              </ul>
            </div>
          </section>

          {/* Section 7 */}
          <section id="str" className="mb-12">
            <h2 className="text-xl font-semibold text-foreground mb-4 flex items-center gap-2">
              <AlertTriangle className="w-5 h-5 text-cyan-400" />
              7. Suspicious Transaction Reporting (STR)
            </h2>
            <div className="space-y-4 text-muted-foreground leading-relaxed">
              <h3 className="text-lg font-medium text-foreground">7.1 Reporting Obligations</h3>
              <p>
                FluxPay is required to file Suspicious Transaction Reports with FINTRAC when there are reasonable grounds to suspect that a transaction is related to money laundering or terrorist financing. STRs must be filed within 30 calendar days of the determination that reasonable grounds to suspect exist.
              </p>

              <h3 className="text-lg font-medium text-foreground mt-6">7.2 Tipping-Off Prohibition</h3>
              <div className="p-4 rounded-xl bg-red-500/10 border border-red-500/20">
                <p className="text-red-200 text-sm">
                  <strong>Criminal Offence:</strong> Under section 8 of the PCMLTFA, it is a criminal offence to disclose that a Suspicious Transaction Report has been filed, or to disclose the contents of such a report, with the intent to prejudice a criminal investigation. Violation may result in a fine of up to $2,000,000 and/or imprisonment for up to five (5) years. FluxPay employees must not, under any circumstances, inform a merchant, client, or any third party that an STR has been filed or is being contemplated. This prohibition extends to all forms of communication, including verbal, written, and electronic.
                </p>
              </div>

              <h3 className="text-lg font-medium text-foreground mt-6">7.3 Internal Reporting</h3>
              <ul className="list-disc pl-6 space-y-2">
                <li>All employees must report suspicious activity to the Compliance Officer immediately</li>
                <li>The Compliance Officer reviews and determines whether to file an STR with FINTRAC</li>
                <li>Attempted suspicious transactions must also be reported, even if the transaction was not completed</li>
                <li>Reports are filed within the prescribed timeframe (30 calendar days)</li>
                <li>All reports are documented and retained in accordance with record-keeping requirements</li>
              </ul>
            </div>
          </section>

          {/* Section 7 */}
          <section id="records" className="mb-12">
            <h2 className="text-xl font-semibold text-foreground mb-4 flex items-center gap-2">
              <BookOpen className="w-5 h-5 text-cyan-400" />
              7. Record Keeping
            </h2>
            <div className="space-y-4 text-muted-foreground leading-relaxed">
              <p>FluxPay maintains all records as required by the PCMLTFA and PCMLTFR:</p>
              <ul className="list-disc pl-6 space-y-2">
                <li>Client identification records: minimum 5 years from date of last transaction</li>
                <li>Transaction records: minimum 5 years from date of transaction</li>
                <li>Suspicious transaction reports: minimum 5 years from filing date</li>
                <li>Large cash transaction reports and electronic funds transfer reports</li>
                <li>Compliance program documentation including policies, risk assessments, and training records</li>
              </ul>
            </div>
          </section>

          {/* Section 8 */}
          <section id="sanctions" className="mb-12">
            <h2 className="text-xl font-semibold text-foreground mb-4 flex items-center gap-2">
              <Globe className="w-5 h-5 text-cyan-400" />
              8. Sanctions Screening
            </h2>
            <div className="space-y-4 text-muted-foreground leading-relaxed">
              <h3 className="text-lg font-medium text-foreground">8.1 Screening Requirements</h3>
              <p>FluxPay screens all merchants and transactions against Canadian sanctions lists including:</p>
              <ul className="list-disc pl-6 space-y-2">
                <li>Criminal Code of Canada (Part II.1) listed entities</li>
                <li>United Nations Act (UNA) regulations</li>
                <li>Special Economic Measures Act (SEMA) regulations</li>
                <li>Justice for Victims of Corrupt Foreign Officials Act (JVCFOA) regulations</li>
                <li>Consolidated Canadian Autonomous Sanctions List</li>
              </ul>

              <h3 className="text-lg font-medium text-foreground mt-6">8.2 Screening Process</h3>
              <ul className="list-disc pl-6 space-y-2">
                <li>Screening at onboarding and on an ongoing basis</li>
                <li>Real-time screening of transactions where applicable</li>
                <li>Immediate escalation of potential matches</li>
                <li>Prohibition of transactions involving sanctioned parties or jurisdictions</li>
              </ul>
            </div>
          </section>

          {/* Section 9 */}
          <section id="training" className="mb-12">
            <h2 className="text-xl font-semibold text-foreground mb-4 flex items-center gap-2">
              <GraduationCap className="w-5 h-5 text-cyan-400" />
              9. Training & Compliance
            </h2>
            <div className="space-y-4 text-muted-foreground leading-relaxed">
              <h3 className="text-lg font-medium text-foreground">9.1 Training Program</h3>
              <ul className="list-disc pl-6 space-y-2">
                <li>All employees receive AML/KYC/CTF training upon hiring</li>
                <li>Annual refresher training for all staff</li>
                <li>Specialized training for compliance and operations teams</li>
                <li>Training records maintained as required</li>
              </ul>

              <h3 className="text-lg font-medium text-foreground mt-6">9.2 Compliance Program Effectiveness Review</h3>
              <ul className="list-disc pl-6 space-y-2">
                <li>Independent review of the compliance program every two years as required by PCMLTFA</li>
                <li>Assessment of policies, procedures, risk assessment methodology, and training</li>
                <li>Recommendations implemented and tracked</li>
                <li>Results reported to senior management</li>
              </ul>
            </div>
          </section>

          {/* Section 10 - Contact */}
          <section id="contact" className="mb-12 p-6 rounded-2xl bg-card/80 dark:bg-white/[0.02] border border-border dark:border-white/[0.06]">
            <h2 className="text-xl font-semibold text-foreground mb-4 flex items-center gap-2">
              <Mail className="w-5 h-5 text-cyan-400" />
              10. Contact
            </h2>
            <div className="space-y-3 text-muted-foreground">
              <p>For questions about this AML/KYC Policy or to report suspicious activity, contact:</p>
              <div className="space-y-2">
                <p><strong>FluxPay</strong></p>
                <p>Ideation House Financial Services Limited</p>
                <p>3080 Yonge Street, Suite 6060, Toronto, Ontario, M4N 3N1, Canada</p>
                <p>Email: compliance@fluxpay.online</p>
              </div>
              <div className="mt-4 p-4 rounded-xl bg-cyan-500/10 border border-cyan-500/20">
                <p className="text-cyan-200 text-sm">
                  If you suspect any transaction or activity may be related to money laundering or terrorist financing, please contact our compliance team immediately at compliance@fluxpay.online. Do not attempt to investigate on your own.
                </p>
              </div>
            </div>
          </section>
        </div>

        {/* Footer Navigation */}
        <div className="pt-8 border-t border-border dark:border-white/[0.06]">
          <div className="flex flex-wrap items-center gap-4 mb-4">
            <Link href="/terms" className="text-cyan-400 hover:text-cyan-300 transition-colors text-sm">
              Terms of Service
            </Link>
            <span className="text-gray-600">&middot;</span>
            <Link href="/privacy" className="text-cyan-400 hover:text-cyan-300 transition-colors text-sm">
              Privacy Policy
            </Link>
            <span className="text-gray-600">&middot;</span>
            <Link href="/refund-policy" className="text-cyan-400 hover:text-cyan-300 transition-colors text-sm">
              Refund Policy
            </Link>
          </div>
          <div className="flex justify-end">
            <Link href="/auth/signin" className="px-6 py-2 rounded-xl bg-cyan-500/10 border border-cyan-500/20 text-cyan-400 hover:bg-cyan-500/20 transition-all">
              Back to Sign In
            </Link>
          </div>
        </div>
      </main>
    </div>
  )
}
